/**
 * SlowmoFXPipeline — Single-pass post-FX shader for impact slow-motion.
 *
 * Combines in one draw call:
 *   1. Vignette (edge darkening)
 *   2. Bloom/glow (bright areas bleed outward)
 *   3. Chromatic aberration (R/B channel split, stronger at edges)
 *   4. Barrel distortion (subtle fisheye)
 *
 * All effects controlled by a single `uIntensity` uniform (0 = off, 1 = full).
 * Applied to the main camera and driven by SlowmoFXManager each frame.
 */

import Phaser from 'phaser';

const FRAG_SHADER = `
precision mediump float;

uniform sampler2D uMainSampler;
uniform vec2 uResolution;
uniform float uIntensity;   // 0–1: master effect strength

varying vec2 outTexCoord;

// Barrel distortion helper
vec2 barrelDistort(vec2 uv, float k) {
  vec2 center = uv - 0.5;
  float r2 = dot(center, center);
  return uv + center * r2 * k;
}

void main() {
  float i = uIntensity;
  if (i < 0.001) {
    gl_FragColor = texture2D(uMainSampler, outTexCoord);
    return;
  }

  // ── 1. Barrel distortion (very subtle — CRT already has its own) ──
  float barrelAmt = i * 0.15;
  vec2 uv = barrelDistort(outTexCoord, barrelAmt);

  // Discard warped corners
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
    return;
  }

  // ── 2. Chromatic aberration (R/B split, stronger at edges) ──
  vec2 center = uv - 0.5;
  float edgeDist = length(center); // 0 at center, ~0.7 at corners
  // Offset direction: radial from center
  vec2 dir = normalize(center + 0.0001);
  // Max offset ~2 pixels at corners, scaling with intensity and edge distance
  float maxOffset = 2.0 / max(uResolution.x, uResolution.y);
  float aberrStrength = i * edgeDist * 2.0 * maxOffset;
  vec2 aberrOffset = dir * aberrStrength;

  float r = texture2D(uMainSampler, uv + aberrOffset).r;
  float g = texture2D(uMainSampler, uv).g;
  float b = texture2D(uMainSampler, uv - aberrOffset).b;
  vec3 color = vec3(r, g, b);

  // ── 3. Bloom/glow (simple bright pass + blur approximation) ──
  // Sample 4 offset neighbors for cheap blur
  float bloomStr = i * 0.35;
  vec2 texel = 1.0 / uResolution;
  float blurR = 3.0; // blur radius in texels
  vec3 blur = vec3(0.0);
  blur += texture2D(uMainSampler, uv + vec2( blurR,  0.0) * texel).rgb;
  blur += texture2D(uMainSampler, uv + vec2(-blurR,  0.0) * texel).rgb;
  blur += texture2D(uMainSampler, uv + vec2( 0.0,  blurR) * texel).rgb;
  blur += texture2D(uMainSampler, uv + vec2( 0.0, -blurR) * texel).rgb;
  blur += texture2D(uMainSampler, uv + vec2( blurR,  blurR) * texel * 0.707).rgb;
  blur += texture2D(uMainSampler, uv + vec2(-blurR,  blurR) * texel * 0.707).rgb;
  blur += texture2D(uMainSampler, uv + vec2( blurR, -blurR) * texel * 0.707).rgb;
  blur += texture2D(uMainSampler, uv + vec2(-blurR, -blurR) * texel * 0.707).rgb;
  blur /= 8.0;
  // Additive bloom: bright areas glow
  vec3 bloomColor = max(blur - 0.4, 0.0) * 2.5;
  color += bloomColor * bloomStr;

  // ── 4. Vignette (darken edges with colored tint) ──
  float vigStr = i * 0.7;
  vec2 vigUV = outTexCoord * (1.0 - outTexCoord);
  float vig = vigUV.x * vigUV.y * 15.0;
  vig = pow(vig, 0.25 + vigStr * 0.5);
  // Tint vignette slightly blue-ish for cinematic feel
  vec3 vigColor = mix(vec3(1.0), vec3(0.7, 0.75, 1.0), vigStr * 0.5);
  color *= vig * vigColor;

  // Slight desaturation for dramatic feel
  float lum = dot(color, vec3(0.299, 0.587, 0.114));
  color = mix(color, vec3(lum), i * 0.15);

  gl_FragColor = vec4(color, 1.0);
}
`;

export class SlowmoFXPipeline extends Phaser.Renderer.WebGL.Pipelines.PostFXPipeline {
  /** Current intensity (set each frame by SlowmoFXManager) */
  intensity = 0;

  constructor(game: Phaser.Game) {
    super({
      game,
      fragShader: FRAG_SHADER,
    });
  }

  onPreRender(): void {
    this.set2f('uResolution', this.renderer.width, this.renderer.height);
    this.set1f('uIntensity', this.intensity);
  }

  // ── Static helpers ──

  static register(scene: Phaser.Scene): void {
    const renderer = scene.game.renderer;
    if (!(renderer instanceof Phaser.Renderer.WebGL.WebGLRenderer)) return;
    if (renderer.pipelines.has('SlowmoFXPipeline')) return;
    renderer.pipelines.addPostPipeline('SlowmoFXPipeline', SlowmoFXPipeline);
  }
}
